import { financialItemPath } from 'src/financial/financial-data/financial-item/financialItemPaths';
import { Box, Flex, Group, Paper, Text } from '@mantine/core';
import { useNavigate } from 'react-router';
import { IconArrowRight, IconUmbrella } from '@tabler/icons-react';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { buildCurrencyFormatter } from 'src/common/currency';
import { camelCaseOrSnakeCaseToWords } from 'src/data-capture/merge-review/ReviewHelperFunctions';
import { Protection } from 'src/financial/financial-data/hooks/useFinancialItems';
import { useCurrency } from 'src/common/hooks/useCurrency';

type Props = {
  policies: Protection[] | undefined;
  clientFirstName: string;
};

export default function ProtectionPoliciesContentNew({ policies, clientFirstName }: Props) {
  const currency = useCurrency();
  const currencyFormatter = buildCurrencyFormatter(currency);
  const { clientId } = useCurrentClient();
  const navigate = useNavigate();

  const handlePolicyClick = (policy: Protection) => {
    navigate(financialItemPath(clientId, policy.product, policy.id));
  };

  const renderedPolicies = () => {
    if (!policies || policies.length === 0) {
      return (
        <Text size={'sm'} c={'475467'}>
          There are no protection policies for {clientFirstName}
        </Text>
      );
    }

    return policies.map(policy => (
      <Paper
        miw={330}
        key={policy.id}
        variant="content"
        radius={'lg'}
        p={'xs'}
        onClick={() => handlePolicyClick(policy)}
        style={{
          cursor: 'pointer',
        }}
        data-testid={'protection-policy'}
      >
        <Flex direction="column" justify="space-between" gap="sm">
          <Text fw={600}>{policy.description ?? camelCaseOrSnakeCaseToWords(policy.type)}</Text>
          <Flex direction="row" wrap="nowrap" justify="space-between">
            <Box>
              <Text fw={600} size="xs" c="gray.6">
                Coverage amount
              </Text>
              <Text size={'lg'} c={'gray.10'} fw={600}>
                {currencyFormatter.format(policy.coverAmount || 0)}
              </Text>
            </Box>
            <Flex align={'center'} gap={'xxs'} mt={'sm'}>
              <Text fw={500} size="xs" c="gray.9">
                Open
              </Text>
              <IconArrowRight stroke={1} size={15} />
            </Flex>
          </Flex>
        </Flex>
      </Paper>
    ));
  };

  if (!clientId) {
    return null;
  }

  return (
    <Paper
      variant="content"
      px="sm"
      pt="sm"
      pb="lg"
      style={{ position: 'relative', overflow: 'hidden' }}
      data-testid="protection-policies"
    >
      <Box
        h={20}
        w="100%"
        bg="brand.1"
        bd="0 0 1px 0 solid var(--mantine-color-gray-3)"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
        }}
      />
      <Flex gap="md" ml={-4}>
        <Flex
          w={58}
          h={58}
          mt={-4}
          style={{
            backgroundColor: 'white',
            borderRadius: 8,
            border: '1.25px solid var(--mantine-color-gray-2)',
            boxShadow: 'var(--mantine-shadow-xs-with-inner)',
            zIndex: 1,
          }}
          align="center"
          justify="center"
        >
          <IconUmbrella size={24} />
        </Flex>
        <Text size={'lg'} fw={600} mt="xs">
          Protection policies
        </Text>
      </Flex>
      <Group mt={'md'}>{renderedPolicies()}</Group>
    </Paper>
  );
}
